import { useCallback, type FC, type KeyboardEvent } from 'react'

import { useTriggerEvent } from 'hooks/useTriggerEvent'
import { useExpandedReferences, useOnClickReferenceExpansionToggle } from 'stores/useActiveMessageAndReferenceStore'
import { MessageReference } from 'types/Api'
import { getTrackingPropsForReferenceEvents } from 'utils/getTrackingPropsForReferenceEvents'

import { ReferenceHeader } from './ReferenceHeader'
import { ReferencePreview } from './ReferencePreview'

type Props = {
  messageId: string
  reference: MessageReference
}

export const Reference: FC<Props> = ({
  messageId,
  reference,
}) => {
  const triggerEvent = useTriggerEvent()

  const expandedReferences = useExpandedReferences()

  const isExpanded = Boolean(expandedReferences.find(expandedReference => messageId === expandedReference.messageId && reference.refId === expandedReference.referenceId))

  const onClickReferenceExpansionToggle = useOnClickReferenceExpansionToggle()

  const handleClickExpansionToggle = useCallback(() => {
    if (!isExpanded) {
      triggerEvent({
        name: 'kai_reference_expanded',
        properties: getTrackingPropsForReferenceEvents({ reference, source: 'references_panel' }),
      })
    }
    onClickReferenceExpansionToggle(messageId, reference.refId)
  }, [isExpanded, messageId, onClickReferenceExpansionToggle, reference, triggerEvent])

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') handleClickExpansionToggle()
  }

  return (
    <div className="mx-3 flex w-full gap-x-3 rounded-md pb-2 group-last:pb-0">
      <div className="sticky top-18 flex size-6 flex-shrink-0 items-center justify-center rounded-full bg-karnovnote-tab-hover text-xs font-medium text-default-on-bg-karnovnote">
        {reference.order}
      </div>
      <div className="flex w-full flex-col border-b border-black/5 pb-4 group-last:border-none print:mt-6">
        <div className="cursor-pointer" onClick={handleClickExpansionToggle} onKeyDown={handleKeyDown} role="button" tabIndex={0}>
          <ReferenceHeader isExpanded={isExpanded} reference={reference} />
        </div>
        {isExpanded && (
          <ReferencePreview messageId={messageId} reference={reference} />
        )}
      </div>
    </div>
  )
}
